import { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Camera, Sparkles, Trash2, Check, RefreshCw, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export function ScanMeal({ reload }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [logging, setLogging] = useState(false);
  const fileRef = useRef(null);
  const today = format(new Date(), 'yyyy-MM-dd');

  const reset = () => { setPreview(null); setResult(null); setScanning(false); };
  const onClose = () => { setOpen(false); reset(); };

  const onPick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPreview(URL.createObjectURL(file));
    setResult(null);
    setScanning(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/nutrition/scan-meal', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!r.data.items || r.data.items.length === 0) {
        toast.info(r.data.note || 'No food detected. Try another photo.');
      }
      setResult(r.data);
    } catch {
      toast.error('Scan failed. Try again with a clearer photo.');
    } finally { setScanning(false); }
  };

  const removeItem = (idx) => {
    const items = result.items.filter((_, i) => i !== idx);
    const total = items.reduce((t, it) => ({
      calories: t.calories + (it.calories || 0), protein: t.protein + (it.protein || 0),
      carbs: t.carbs + (it.carbs || 0), fat: t.fat + (it.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
    setResult({ ...result, items, total });
  };

  const logMeal = async () => {
    if (!result?.items?.length) return;
    setLogging(true);
    try {
      for (const it of result.items) {
        await api.post('/extra-foods', {
          name: it.name, is_custom: true, quantity: null,
          calories: Math.round(it.calories || 0), protein: Math.round(it.protein || 0),
          carbs: Math.round(it.carbs || 0), fat: Math.round(it.fat || 0), date: today,
        });
      }
      toast.success(`Logged ${result.items.length} item${result.items.length > 1 ? 's' : ''} from your scan`);
      onClose(); reload?.();
    } catch { toast.error('Failed to log'); } finally { setLogging(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="scan-meal-btn"
        className="w-full regen-card-elevated p-4 flex items-center gap-3 bg-gradient-to-br from-[var(--c-green-soft)] to-transparent active:scale-[0.99] transition text-left">
        <div className="w-11 h-11 rounded-xl bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0">
          <Camera className="w-5 h-5 text-[var(--c-green-ink)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold flex items-center gap-1.5">Scan Meal <Sparkles className="w-3.5 h-3.5 text-[var(--c-green-ink)]" /></div>
          <div className="text-xs text-muted-foreground">Snap a photo — instantly estimate the macros</div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="scan-meal-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5 text-[var(--c-green-ink)]" /> Scan Meal</DialogTitle></DialogHeader>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" data-testid="scan-file-input" />

          {!preview && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-2xl bg-[var(--c-green-soft)] flex items-center justify-center mx-auto mb-4"><Camera className="w-8 h-8 text-[var(--c-green-ink)]" /></div>
              <p className="text-sm text-muted-foreground mb-4 px-4">Take or upload a photo of your meal and we'll estimate the foods and macros.</p>
              <Button onClick={() => fileRef.current?.click()} data-testid="scan-take-photo-btn" className="bg-[var(--c-green)] text-[#081726] font-semibold"><Camera className="w-4 h-4 mr-2" /> Take / Upload Photo</Button>
            </div>
          )}

          {preview && (
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden h-44">
                <img src={preview} alt="meal" className="w-full h-full object-cover" />
                {scanning && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2" data-testid="scan-loading">
                    <Loader2 className="w-7 h-7 text-[var(--c-green-ink)] animate-spin" />
                    <span className="text-sm font-medium">Analysing your meal…</span>
                  </div>
                )}
              </div>

              {result && !scanning && (
                <>
                  {result.items.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="label-mini">Detected Foods</div>
                        <span className={`pill text-[10px] ${result.confidence === 'high' ? 'bg-[var(--c-green-soft)] text-[var(--c-green-ink)]' : result.confidence === 'low' ? 'bg-[#FF6FA8]/15 text-[#FF6FA8]' : 'bg-[#FFB800]/15 text-[#FFB800]'}`}>{result.confidence} confidence</span>
                      </div>
                      <div className="space-y-2">
                        {result.items.map((it, idx) => (
                          <div key={idx} className="regen-card p-2.5 flex items-center gap-3" data-testid={`scan-item-${idx}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{it.name} <span className="text-muted-foreground font-normal">· {it.quantity}</span></div>
                              <div className="text-[11px] font-mono text-muted-foreground">{Math.round(it.calories)} kcal · P{Math.round(it.protein)} C{Math.round(it.carbs)} F{Math.round(it.fat)}</div>
                            </div>
                            <button onClick={() => removeItem(idx)} data-testid={`scan-remove-${idx}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="regen-card-elevated p-3 grid grid-cols-4 gap-2 text-center">
                        <div><div className="font-mono text-base text-[#FFB800]">{result.total.calories}</div><div className="label-mini text-[9px]">Cal</div></div>
                        <div><div className="font-mono text-base text-[var(--c-green-ink)]">{result.total.protein}g</div><div className="label-mini text-[9px]">Protein</div></div>
                        <div><div className="font-mono text-base text-[#00C8FF]">{result.total.carbs}g</div><div className="label-mini text-[9px]">Carbs</div></div>
                        <div><div className="font-mono text-base text-[#7DD3FC]">{result.total.fat}g</div><div className="label-mini text-[9px]">Fat</div></div>
                      </div>
                      <p className="text-[11px] text-muted-foreground text-center">Estimates are approximate. Remove anything wrong before logging.</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="scan-retry-btn" className="border-[var(--c-hair)] h-11"><RefreshCw className="w-4 h-4 mr-1" /> Rescan</Button>
                        <Button onClick={logMeal} disabled={logging} data-testid="scan-log-btn" className="bg-[var(--c-green)] text-[#081726] font-semibold h-11">{logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Log Meal</>}</Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-sm text-muted-foreground mb-3">{result.note || 'No food detected in this photo.'}</p>
                      <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="scan-retry-btn" className="border-[var(--c-hair)]"><RefreshCw className="w-4 h-4 mr-1" /> Try Another Photo</Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
